import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getArticles } from '../services/api';
import { ArrowRight, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

const BlogSection = () => {
  const { language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await getArticles();
        setArticles(res.data.slice(0, 3)); // Show only 3 latest
      } catch (error) {
        console.error('Failed to load articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  if (loading || articles.length === 0) return null;

  return (
    <section id="blog" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-amber-600 font-semibold tracking-wide uppercase text-sm">
            {language === 'en' ? 'Knowledge Center' : '知识中心'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
            {language === 'en' ? 'Roofing Tips & Guides' : '屋顶知识与指南'}
          </h2>
          <p className="text-slate-600 mt-4 text-lg">
            {language === 'en' 
              ? 'Expert advice to help you make informed decisions about your roof'
              : '专业建议帮助您做出明智的屋顶决定'}
          </p>
        </div>

        {/* Articles Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {articles.map((article) => (
            <Link
              key={article.id}
              to={`/blog/${article.slug}`}
              className="group bg-slate-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              {/* Image */}
              <div className="h-48 overflow-hidden">
                <img
                  src={article.image_url}
                  alt={language === 'en' ? article.title_en : article.title_zh}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 text-amber-600 text-sm mb-3">
                  <BookOpen className="w-4 h-4" />
                  <span>{language === 'en' ? 'Article' : '文章'}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3 group-hover:text-amber-600 transition-colors line-clamp-2">
                  {language === 'en' ? article.title_en : article.title_zh}
                </h3>
                <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                  {language === 'en' ? article.summary_en : article.summary_zh}
                </p>
                <div className="flex items-center text-amber-600 font-medium text-sm group-hover:gap-2 transition-all">
                  {language === 'en' ? 'Read More' : '阅读更多'}
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-8 py-4 rounded-xl transition-colors"
          >
            {language === 'en' ? 'View All Articles' : '查看所有文章'}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
